import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Plus, Edit, Trash2, Download, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { CategorySubcategoryMapping } from '../../types/categoryMapping';
import { CategoryMappingRuleModal } from './CategoryMappingRuleModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { useApp } from '@/contexts/AppContext';
import { useToast } from '@/hooks/use-toast';
import {
  getParentCategories,
  getSubcategories,
  createSubcategory,
  updateSubcategory,
  deleteSubcategory,
} from '@/api/services/transactionCategoryService';
import { ApiError } from '@/api/client';
import type { ParentCategoryListItemApi } from '@/api/models/transactionCategory';
import type { TransactionCategory } from '@/types';

const INITIAL_DEMO_MAPPINGS: CategorySubcategoryMapping[] = [
  {
    id: '1',
    parentCategory: 'Materials',
    subcategoryName: 'Construction Materials',
    description: 'Building materials, concrete, timber, bricks',
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '2',
    parentCategory: 'Materials',
    subcategoryName: 'Small Tools & Consumables',
    description: 'Hand tools, fasteners, adhesives, consumables',
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '3',
    parentCategory: 'Payroll',
    subcategoryName: 'Site Workers',
    description: 'Construction site staff wages',
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '4',
    parentCategory: 'Payroll',
    subcategoryName: 'Office Staff',
    description: 'Administrative and management staff',
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '5',
    parentCategory: 'Equipment',
    subcategoryName: 'Plant Hire',
    description: 'Heavy machinery and equipment rental',
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '6',
    parentCategory: 'Subcontractors',
    subcategoryName: 'Electrical',
    description: 'Electrical subcontractor work',
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '7',
    parentCategory: 'Subcontractors',
    subcategoryName: 'Plumbing',
    description: 'Plumbing subcontractor work',
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
];

type SortField = 'parentCategory' | 'subcategoryName' | 'description' | 'isActive';
type SortDirection = 'asc' | 'desc';

export const CategoryMappingPage: React.FC = () => {
  const { mode } = useApp();
  const { toast } = useToast();
  const isLive = mode === 'live';

  const [mappings, setMappings] = useState<CategorySubcategoryMapping[]>(INITIAL_DEMO_MAPPINGS);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<CategorySubcategoryMapping | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState<string | null>(null);

  // Live-mode state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiParentCategories, setApiParentCategories] = useState<ParentCategoryListItemApi[]>([]);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('parentCategory');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Filter state
  const [parentCategoryFilter, setParentCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [parentRes, subRes] = await Promise.all([
        getParentCategories({ pageSize: 500 }),
        getSubcategories({ pageSize: 500 }),
      ]);

      setApiParentCategories(parentRes.data);

      const mapped: CategorySubcategoryMapping[] = subRes.data.map((sub) => ({
        id: String(sub.id),
        parentCategory: sub.parentCategoryName as TransactionCategory,
        parentCategoryId: sub.parentCategoryId,
        subcategoryName: sub.name,
        description: sub.description ?? undefined,
        isActive: sub.isActive,
        createdAt: new Date(),
        updatedAt: new Date(),
      }));
      setMappings(mapped);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to fetch categories';
      setError(msg);
      setMappings([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isLive) {
      fetchData();
    } else {
      setMappings(INITIAL_DEMO_MAPPINGS);
      setApiParentCategories([]);
      setError(null);
    }
  }, [isLive, fetchData]);

  // Get unique parent categories for filter dropdown
  const uniqueParentCategories = useMemo(() => {
    const categories = new Set(mappings.map(m => m.parentCategory));
    return Array.from(categories).sort();
  }, [mappings]);

  // Handle column sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon for column header
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 inline opacity-30" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 inline" />
      : <ArrowDown className="h-4 w-4 ml-1 inline" />;
  };

  // Filter and sort mappings
  const filteredAndSortedMappings = useMemo(() => {
    let result = [...mappings];

    // Apply parent category filter
    if (parentCategoryFilter !== 'all') {
      result = result.filter(m => m.parentCategory === parentCategoryFilter);
    }

    // Apply status filter
    if (statusFilter === 'active') {
      result = result.filter(m => m.isActive);
    } else if (statusFilter === 'inactive') {
      result = result.filter(m => !m.isActive);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(m => 
        m.subcategoryName.toLowerCase().includes(query) ||
        (m.description?.toLowerCase().includes(query) || false)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      if (sortField === 'isActive') {
        aValue = a.isActive ? 1 : 0;
        bValue = b.isActive ? 1 : 0;
      } else {
        aValue = (a[sortField] || '').toLowerCase();
        bValue = (b[sortField] || '').toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [mappings, parentCategoryFilter, statusFilter, searchQuery, sortField, sortDirection]);

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['Parent Category', 'Subcategory', 'Description', 'Status'];
    const rows = filteredAndSortedMappings.map(m => [
      m.parentCategory,
      m.subcategoryName,
      m.description || '',
      m.isActive ? 'Active' : 'Inactive'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    // Copy to clipboard (workaround for sandbox download restrictions)
    navigator.clipboard.writeText(csvContent).then(() => {
      alert('✅ CSV data copied to clipboard!\n\nYou can now paste it into Excel, Google Sheets, or a text editor.\n\nNote: Direct downloads are disabled in the Evo Builder preview environment, but will work in production.');
    }).catch((err) => {
      console.error('Failed to copy to clipboard:', err);
      alert('❌ Failed to copy to clipboard. Please check browser permissions.');
    });
  };

  const handleAddMapping = () => {
    setEditingMapping(null);
    setIsModalOpen(true);
  };

  const handleEditMapping = (mapping: CategorySubcategoryMapping) => {
    setEditingMapping(mapping);
    setIsModalOpen(true);
  };

  const handleSaveMapping = async (mappingData: Omit<CategorySubcategoryMapping, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (isLive) {
      try {
        if (editingMapping) {
          const numericId = Number(editingMapping.id);
          await updateSubcategory(numericId, {
            name: mappingData.subcategoryName,
            description: mappingData.description ?? null,
            isActive: mappingData.isActive,
            parentCategoryId: mappingData.parentCategoryId ?? null,
          });
          toast({ title: 'Updated', description: `Subcategory "${mappingData.subcategoryName}" updated successfully.` });
        } else {
          if (!mappingData.parentCategoryId) return;
          await createSubcategory({
            name: mappingData.subcategoryName,
            parentCategoryId: mappingData.parentCategoryId,
            description: mappingData.description ?? null,
            isActive: mappingData.isActive,
          });
          toast({ title: 'Created', description: `Subcategory "${mappingData.subcategoryName}" created successfully.` });
        }
        await fetchData();
      } catch (err) {
        const description = err instanceof ApiError ? err.message : 'Failed to save subcategory. Please try again.';
        toast({ title: 'Error', description, variant: 'destructive' });
      }
    } else {
      if (editingMapping) {
        setMappings(mappings.map(m => 
          m.id === editingMapping.id 
            ? { ...m, ...mappingData, updatedAt: new Date() }
            : m
        ));
      } else {
        const newMapping: CategorySubcategoryMapping = {
          ...mappingData,
          id: Date.now().toString(),
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        setMappings([...mappings, newMapping]);
      }
    }
  };

  const handleDeleteClick = (mappingId: string) => {
    setMappingToDelete(mappingId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (mappingToDelete) {
      if (isLive) {
        try {
          await deleteSubcategory(Number(mappingToDelete));
          toast({ title: 'Deleted', description: 'Subcategory deleted successfully.' });
          await fetchData();
        } catch (err) {
          const description = err instanceof ApiError ? err.message : 'Failed to delete subcategory. Please try again.';
          toast({ title: 'Error', description, variant: 'destructive' });
        }
      } else {
        setMappings(mappings.filter(m => m.id !== mappingToDelete));
      }
    }
    setShowDeleteConfirm(false);
    setMappingToDelete(null);
  };

  const handleToggleActive = async (mappingId: string) => {
    if (isLive) {
      const mapping = mappings.find(m => m.id === mappingId);
      if (!mapping) return;
      try {
        await updateSubcategory(Number(mappingId), { isActive: !mapping.isActive });
        toast({ title: 'Updated', description: `Subcategory "${mapping.subcategoryName}" ${mapping.isActive ? 'deactivated' : 'activated'}.` });
        await fetchData();
      } catch (err) {
        const description = err instanceof ApiError ? err.message : 'Failed to update subcategory status.';
        toast({ title: 'Error', description, variant: 'destructive' });
      }
    } else {
      setMappings(mappings.map(m => 
        m.id === mappingId 
          ? { ...m, isActive: !m.isActive, updatedAt: new Date() }
          : m
      ));
    }
  };

  const mappingToDeleteData = mappings.find(m => m.id === mappingToDelete);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Parent Child Categories</h1>
          <p className="text-muted-foreground mt-1">
            Optionally break down parent categories into more granular child values for cash flow analysis
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={handleAddMapping}>
            <Plus className="h-4 w-4 mr-2" />
            Add Child Category
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        <span className="font-medium">This is an optional feature.</span> If your parent categories from your accounting system are already granular enough for cash flow analysis, you don't need to configure anything here. Use this area only if you want to break a parent category down into more specific child values — transactions can then be reassigned to a child category on the Transactions screen.
      </div>

      {isLive && apiParentCategories.length === 0 && !isLoading && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="font-medium">No parent categories have been synced yet.</span> Connect a data source and enable Parent Category Source to populate the parent category list.
        </div>
      )}

      {isLive && isLoading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading categories...</div>
      ) : isLive && error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <p className="font-medium">Failed to load categories</p>
          <p className="text-xs mt-1">{error}</p>
        </div>
      ) : (
      <Card>
        <CardHeader>
          <CardTitle>Parent Child Categories</CardTitle>
          <div className="flex gap-4 mt-4">
            <div className="flex-1">
              <Select value={parentCategoryFilter} onValueChange={setParentCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Parent Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {uniqueParentCategories.map(category => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active Only</SelectItem>
                  <SelectItem value="inactive">Inactive Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Input
                placeholder="Search subcategory or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer select-none hover:bg-muted/50"
                  onClick={() => handleSort('parentCategory')}
                >
                  Parent Category
                  {getSortIcon('parentCategory')}
                </TableHead>
                <TableHead 
                  className="cursor-pointer select-none hover:bg-muted/50"
                  onClick={() => handleSort('subcategoryName')}
                >
                  Subcategory
                  {getSortIcon('subcategoryName')}
                </TableHead>
                <TableHead 
                  className="cursor-pointer select-none hover:bg-muted/50"
                  onClick={() => handleSort('description')}
                >
                  Description
                  {getSortIcon('description')}
                </TableHead>
                <TableHead 
                  className="cursor-pointer select-none hover:bg-muted/50"
                  onClick={() => handleSort('isActive')}
                >
                  Status
                  {getSortIcon('isActive')}
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedMappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    {searchQuery || parentCategoryFilter !== 'all' || statusFilter !== 'all'
                      ? 'No subcategories match your filters.'
                      : 'No subcategories defined. Click "Add New Mapping" to create one.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedMappings.map((mapping) => (
                  <TableRow key={mapping.id}>
                    <TableCell>
                      <Badge variant="secondary">{mapping.parentCategory}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{mapping.subcategoryName}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {mapping.description || '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={mapping.isActive}
                          onCheckedChange={() => handleToggleActive(mapping.id)}
                        />
                        <span className="text-sm text-muted-foreground">
                          {mapping.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditMapping(mapping)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(mapping.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      )}

      <CategoryMappingRuleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveMapping}
        editingRule={editingMapping}
        parentCategories={isLive
          ? apiParentCategories.map(c => ({ id: c.id, name: c.name }))
          : undefined}
      />

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Child Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the subcategory "{mappingToDeleteData?.subcategoryName}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
